import type { MouseEvent } from "react";
import styled from "styled-components";
import { Flex } from "orcs-design-system";

import {
  TreeNodeLabel,
  TreeRowLinkLabel,
  isPlainLeftClick,
} from "src/shared/TreeViewNext";
import type { TreeNodeData } from "src/shared/TreeViewNext";
import useObjectiveProfileConfig from "src/commonComponents/ObjectiveProfileHeader/hooks/useObjectiveProfileConfig";
import BadgesDisplay from "src/components/BadgesDisplay";
import { OBJECTIVE_ATTRIBUTE_TYPE } from "src/consts/objectives";
import type { BadgeConfigItem } from "src/types/badgesDisplay";

type GoalTreeNode = TreeNodeData & {
  objective?: Record<string, unknown> | undefined;
  group?: Record<string, unknown> | undefined;
  isLoadMore?: boolean | undefined;
  noActivation?: boolean | undefined;
};

const GoalTreeHeaderTitleLink = styled(Flex).attrs({ as: "a" })`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  font-family: inherit;
  align-items: center;
  min-width: 0;
  flex: 1;
  text-decoration: none;
  color: inherit;
  padding: 0;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

type ObjectiveTreeHeaderProps = {
  node: GoalTreeNode;
  onActivate?: (() => void) | undefined;
  getGoalLinkTo?: ((node: GoalTreeNode) => string | null) | undefined;
};

const ObjectiveTreeHeader = ({
  node,
  onActivate,
  getGoalLinkTo,
}: ObjectiveTreeHeaderProps) => {
  const { badgeConfig } = useObjectiveProfileConfig(node["objective"]) as {
    badgeConfig?: BadgeConfigItem[] | null | undefined;
  };
  const treeBadgeConfig: BadgeConfigItem[] | undefined = badgeConfig?.filter(
    (b): b is BadgeConfigItem => b.id !== OBJECTIVE_ATTRIBUTE_TYPE.STATUS
  );

  const goalHref = getGoalLinkTo?.(node) ?? null;
  const displayName = typeof node.name === "string" ? node.name : undefined;

  const label = <TreeNodeLabel node={node} />;
  const linkLabel = (
    <TreeRowLinkLabel
      data-testid="treeheader-node-name"
      data-tree-row-content
      $fillRow
      title={displayName}
    >
      {displayName}
    </TreeRowLinkLabel>
  );
  const badges =
    node["objective"] && treeBadgeConfig ? (
      <BadgesDisplay badgeConfig={treeBadgeConfig} />
    ) : null;

  if (goalHref) {
    return (
      <Flex alignItems="center" width="100%" gap="xs" minWidth={0}>
        <GoalTreeHeaderTitleLink
          data-tree-row-action
          href={goalHref}
          onClick={(e: MouseEvent) => {
            if (!isPlainLeftClick(e)) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            onActivate?.();
          }}
        >
          {linkLabel}
        </GoalTreeHeaderTitleLink>
        {badges}
      </Flex>
    );
  }

  return (
    <Flex alignItems="center" width="100%" gap="xs">
      {label}
      {badges}
    </Flex>
  );
};

export default ObjectiveTreeHeader;
